import axios from "axios";
import { isOnline, setOnline, offlinePut, offlineGet, offlineError, markSync, offlineClear, onOnlineChange } from "@/lib/offline";

export const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "pg_token";

const EMP_KEY = "pg_emp_token";
export const getAccountToken = () => localStorage.getItem(TOKEN_KEY);
export const getEmpToken = () => localStorage.getItem(EMP_KEY);
/** Token used for requests: the identified employee's, else the shared account's (the API answers 423 pin_required). */
export const getToken = () => getEmpToken() || getAccountToken();
export const setToken = (t) => (t ? localStorage.setItem(TOKEN_KEY, t) : (localStorage.removeItem(TOKEN_KEY), localStorage.removeItem(EMP_KEY)));
export const setEmpToken = (t) => (t ? localStorage.setItem(EMP_KEY, t) : localStorage.removeItem(EMP_KEY));

export const api = axios.create({ baseURL: API });
const unlockWaiters = [];
const waitForUnlock = () => (getEmpToken() ? Promise.resolve() : new Promise((res, rej) => unlockWaiters.push({ res, rej })));
window.addEventListener("pg-pin-unlocked", () => { unlockWaiters.splice(0).forEach((w) => w.res()); });
window.addEventListener("pg-logout", () => { unlockWaiters.splice(0).forEach((w) => w.rej(Object.assign(new Error("canceled"), { code: "ERR_CANCELED" }))); });
const DEVICE_KEY = "pg_device";
export const deviceId = () => { let d = localStorage.getItem(DEVICE_KEY); if (!d) { d = (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`); localStorage.setItem(DEVICE_KEY, d); } return d; };
const offKey = (cfg) => `${cfg.url}?${JSON.stringify(cfg.params || {})}`;
const cacheable = (cfg) => (cfg.method || "get").toLowerCase() === "get" && !cfg.responseType && !/presence|\/auth\/login|invoice|\/files\//.test(cfg.url || "");
api.interceptors.request.use((cfg) => {
  const t = getToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  cfg.headers["X-Device-Id"] = deviceId();
  // While the employee is locked, business requests would only get 423 and race the unlock: park them until the PIN is entered, then send with the fresh token.
  if (!getEmpToken() && getAccountToken() && !/\/auth\//.test(cfg.url || "")) {
    const real = cfg.adapter || axios.defaults.adapter;
    cfg.adapter = (c) => waitForUnlock().then(() => { c.headers.Authorization = `Bearer ${getToken()}`; return axios.getAdapter(real)(c); });
  }
  if ((cfg.method || "get").toLowerCase() !== "get" && !isOnline()) return Promise.reject(offlineError());
  return cfg;
});
api.interceptors.response.use(
  (r) => { if (r.config.method !== "get") patchCache(r); setOnline(true); if (cacheable(r.config) && getToken()) { markSync(); offlinePut(offKey(r.config), r.data); } return r; },
  async (err) => {
    if (err.response?.status === 423 && err.response?.data?.detail?.code === "pin_required") {
      // Only a request made WITH the current employee token means that identity is gone; stale replies from the locked state must not undo a fresh unlock.
      const sentWith = (err.config?.headers?.Authorization || "").replace("Bearer ", "");
      const emp = getEmpToken();
      if (emp && sentWith === emp) { setEmpToken(null); window.dispatchEvent(new Event("pg-pin-required")); }
      return Promise.reject(err);
    }
    if (err.response?.status === 401 && getToken() && err.response?.data?.detail?.code !== "reverify_required" && !/\/auth\/pin/.test(err.config?.url || "")) {
      setToken(null);
      window.dispatchEvent(new Event("pg-logout"));
    }
    if (!err.response && !err.offline && err.code !== "ERR_CANCELED") {
      setOnline(false);
      if (cacheable(err.config || {})) { const hit = await offlineGet(offKey(err.config)); if (hit) return { data: hit.data, status: 200, statusText: "offline", headers: {}, config: err.config, offline: true }; }
    }
    return Promise.reject(err);
  }
);

const CACHE_MAX = 300;
const cache = new Map();
export const invalidateCache = () => cache.clear();
// After a write: keep cached lists on screen (instant back navigation) but refresh the edited record inside them.
// Every cachedGet still re-fetches in the background, so lists never stay stale.
function patchCache(r) {
  if (/presence/.test(r.config.url || "")) return;
  const doc = r.data;
  if (!doc || typeof doc !== "object" || !doc.id) return;
  for (const [k, v] of cache) {
    if (Array.isArray(v?.items) && v.items.some((it) => it?.id === doc.id)) cache.set(k, { ...v, items: v.items.map((it) => (it.id === doc.id ? { ...it, ...doc } : it)) });
    else if (v?.id === doc.id) cache.set(k, { ...v, ...doc });
  }
}
export function cachedGet(url, config, set) {
  const key = `${url}?${JSON.stringify(config?.params || {})}`;
  if (cache.has(key)) set(cache.get(key), true);
  return api.get(url, config).then((r) => {
    if (cache.size >= CACHE_MAX) cache.delete(cache.keys().next().value);
    cache.set(key, r.data); set(r.data, false); return r;
  });
}
window.addEventListener("pg-logout", () => { invalidateCache(); offlineClear(); });

export function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (!d) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x.msg || JSON.stringify(x)).join("; ");
  if (d.message) return d.message;
  return JSON.stringify(d);
}


export async function downloadBlob(path, filename) {
  const r = await api.get(path, { responseType: "blob" });
  const url = URL.createObjectURL(r.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export const money = (n) => { const v = Number(n || 0); return `${v < 0 ? "−" : ""}$${Math.abs(v).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`; };
export const fmtDate = (d) => (d ? new Date(d + (d.length === 10 ? "T00:00:00" : "")).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—");
export const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  return `${((h + 11) % 12) + 1}:${String(m).padStart(2, "0")} ${h >= 12 ? "PM" : "AM"}`;
};
export const todayISO = () => new Date().toLocaleDateString("en-CA");
export const nowHHMM = () => { const d = new Date(); return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`; };
export const initials = (f = "", l = "") => `${f[0] || ""}${l[0] || ""}`.toUpperCase();
export const bn = (n) => String(n ?? "").padStart(3, "0");

export const STATUS = {
  draft: { label: "Draft", bg: "#F3F4F6", text: "#374151", border: "#D1D5DB" },
  sale_only: { label: "Quick Sale Only", hint: "Archives automatically when paid · not saved to calendar", bg: "#FFFFFF", text: "#1F2937", border: "#CBD5E1" },
  reserved: { label: "Reserved / Confirmed", bg: "#E0F2FE", text: "#075985", border: "#7DD3FC" },
  working: { label: "Working", bg: "#FEF3C7", text: "#92400E", border: "#FCD34D" },
  ready_for_pickup: { label: "Ready for Pickup", bg: "#D1FAE5", text: "#065F46", border: "#6EE7B7" },
  picked_up: { label: "Picked Up", bg: "#FFEDD5", text: "#9A3412", border: "#FDBA74" },
  returned: { label: "Returned", hint: "Archives automatically when paid", bg: "#E4E4E7", text: "#3F3F46", border: "#A1A1AA" },
  archived: { label: "Archived", bg: "#F1F5F9", text: "#334155", border: "#94A3B8" },
};
export const fmtDateTime = (iso) => (iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : "—");
export const ITEM_TYPES = { rental: "Rental Inventory", tracked: "Sale Item Inventory Tracked", untracked: "Sale Item Inventory Untracked" };
export const priceVaries = (i) => { if (!i.sizes?.length) return false; const ps = i.sizes.map((s) => (s.price !== null && s.price !== undefined && s.price !== "" ? Number(s.price) : Number(i.base_price || 0))); return ps.some((p) => p !== ps[0]); };
export const priceFrom = (i) => (i.sizes?.length ? Math.min(...i.sizes.map((s) => (s.price !== null && s.price !== undefined && s.price !== "" ? Number(s.price) : Number(i.base_price || 0)))) : Number(i.base_price || 0));

const bus = new EventTarget();
onOnlineChange(() => { if (isOnline()) { invalidateCache(); bus.dispatchEvent(new Event("changed")); } });
export const onDataChanged = (fn) => { bus.addEventListener("changed", fn); const st = (e) => e.key === "pg_changed" && fn(); window.addEventListener("storage", st); return () => { bus.removeEventListener("changed", fn); window.removeEventListener("storage", st); }; };
export const emitDataChanged = () => { invalidateCache(); bus.dispatchEvent(new Event("changed")); };
/** Cross-device live sync: poll the server change counter and fire `changed` when another session wrote something. */
export const startLiveSync = (intervalMs = 4000) => {
  let last = null, stop = false, busy = false;
  const tick = async () => { if (stop || busy || document.visibilityState !== "visible" || !isOnline() || !getToken()) return; busy = true; try { const { data } = await api.get("/sync/version"); if (last !== null && data.v !== last) emitDataChanged(); last = data.v; } catch {} finally { busy = false; } };
  tick(); const iv = setInterval(tick, intervalMs); const vis = () => document.visibilityState === "visible" && tick(); document.addEventListener("visibilitychange", vis); window.addEventListener("focus", vis);
  return () => { stop = true; clearInterval(iv); document.removeEventListener("visibilitychange", vis); window.removeEventListener("focus", vis); };
};
api.interceptors.response.use((r) => { const m = (r.config?.method || "get").toLowerCase(); if (m !== "get" && /\/(bookings|inventory|customers|bulk|appointments)/.test(r.config?.url || "") && !/presence|history\b|appointments\/(check|upcoming|availability)/.test(r.config?.url || "")) { bus.dispatchEvent(new Event("changed")); try { localStorage.setItem("pg_changed", String(Date.now())); } catch {} } return r; });
